// Desktop "Canvas Dimensions" panel. Enter an object's real Width/Depth/Height
// (free-text: 48 3/4", 5' 6", 1.5 m, 150 cm…) and the real size of one voxel; the
// work volume resizes to those proportions so the walls mark the boundary to draw
// within. The voxel size is proportion-independent — a 1" voxel is 1" whatever the
// object's shape — and the per-axis cell counts fall out of it, capped at
// MAX_CELLS. DOM-only (desktop). On Apply it hands main the computed voxel grid +
// the voxel→real scale.

import {
  type LengthUnit,
  formatLengthIn,
  parseLength,
} from "../model/units";
import { createSegToggle } from "./mode-switch";
import { MAX_VOLUME_CELLS } from "../scene/work-volume-bounds";

export interface DimensionsResult {
  cellsX: number; // Width  (X)
  cellsY: number; // Height (Y)
  cellsZ: number; // Depth  (Z)
  voxelInches: number; // real inches per voxel
  unit: LengthUnit; // unit to display measurements in
  // Exact entered real dimensions (inches), so placements can be scaled to the
  // precise size — not just the voxel-rounded approximation.
  realIn: { w: number; d: number; h: number };
}

// The current canvas's values (inches), used to prefill the fields each time the
// panel opens so it reflects the model being edited rather than the last input.
export interface DimensionsInitial {
  unit: LengthUnit;
  widthIn: number;
  depthIn: number;
  heightIn: number;
  voxelIn: number;
}

export interface DimensionsPanel {
  // `initial` prefills the fields from the current canvas. `onApply` overrides
  // the default apply handler for this opening only (used by "New model" to
  // clear + resize instead of just resize).
  open(initial?: DimensionsInitial, onApply?: (r: DimensionsResult) => void): void;
  close(): void;
  isOpen(): boolean;
}

// The per-axis cell cap is owned by the work-volume bounds (the resize tool's
// limit); import it so this panel's clamp can never drift from it.
const MAX_CELLS = MAX_VOLUME_CELLS;

// Voxel detail is entered as a DENSITY: voxels per inch (imperial) or per cm
// (metric). Convert to/from the real voxel size in inches the model stores.
// 1 inch density-unit = 1 inch; 1 cm density-unit = 1/2.54 inch.
const inchPerDensityUnit = (u: LengthUnit): number => (u === "m" ? 1 / 2.54 : 1);
const densityToVoxelInches = (density: number, u: LengthUnit): number =>
  inchPerDensityUnit(u) / density;
const voxelInchesToDensity = (voxelIn: number, u: LengthUnit): number =>
  inchPerDensityUnit(u) / voxelIn;
// Tidy a density value for display (≤3 decimals, no trailing zeros).
const fmtDensity = (d: number): string => String(parseFloat(d.toFixed(3)));

const CSS = `
/* Dimmed full-screen backdrop behind the panel (matches the modal dialogs). Sits
   above the sidebar (z 10) + dock (z 11), below the panel (z 20). */
#dt-dims-backdrop {
  position: fixed; inset: 0; z-index: 19; display: none;
  background: rgba(0, 0, 0, 0.4);
}
#dt-dims-backdrop.open { display: block; }
#dt-dims {
  position: fixed; top: 50%; left: 50%; transform: translate(-50%, -50%); z-index: 20;
  width: 320px; box-sizing: border-box; padding: 16px 18px;
  display: none; flex-direction: column; gap: 11px;
  border-radius: 14px; border: 1px solid rgba(255,255,255,0.14);
  background: rgba(28,30,38,0.86); backdrop-filter: blur(16px);
  -webkit-backdrop-filter: blur(16px); box-shadow: 0 8px 32px rgba(0,0,0,0.45);
  font: 13px/1.4 -apple-system, system-ui, 'SF Pro Text', sans-serif;
  color: rgba(236,239,246,0.96); user-select: none;
}
#dt-dims.open { display: flex; }
#dt-dims h3 { margin: 0; font-size: 14px; font-weight: 650; }
#dt-dims .sub { color: rgba(236,239,246,0.5); font-size: 11px; margin-top: -6px; }
#dt-dims .row { display: flex; align-items: center; gap: 8px; }
#dt-dims .row label { width: 58px; color: rgba(236,239,246,0.7); }
#dt-dims input {
  flex: 1; min-width: 0; font: inherit; color: #eceff6;
  background: rgba(255,255,255,0.06); border: 1px solid rgba(255,255,255,0.14);
  border-radius: 7px; padding: 6px 8px;
}
#dt-dims input.bad { border-color: #e0556a; }
#dt-dims .detail input { flex: none; width: 88px; }
#dt-dims .preview {
  font-variant-numeric: tabular-nums; color: rgba(236,239,246,0.8);
  background: rgba(255,255,255,0.05); border-radius: 8px; padding: 8px 10px;
  min-height: 16px; line-height: 1.5;
}
#dt-dims .btns { display: flex; justify-content: flex-end; gap: 8px; margin-top: 2px; }
/* Scoped to the action row so the ID specificity doesn't override the unit
   toggle's own (segmented pill) styling — incl. its active highlight. */
#dt-dims .btns button {
  font: inherit; cursor: pointer; border-radius: 8px; padding: 7px 14px;
  border: 1px solid rgba(255,255,255,0.14); background: #34384a; color: #d7dae6;
}
#dt-dims .btns button:hover { background: #424862; }
#dt-dims .btns button.primary { background: #6fa0ff; color: #11141c; border-color: #6fa0ff; }
#dt-dims .btns button.primary:disabled { opacity: 0.4; cursor: not-allowed; }
`;

export function installDimensionsPanel(opts: {
  onApply: (r: DimensionsResult) => void;
}): DimensionsPanel {
  const style = document.createElement("style");
  style.textContent = CSS;
  document.head.appendChild(style);

  // Dimmed backdrop behind the panel; clicking it closes (like the modal dialogs).
  const backdrop = document.createElement("div");
  backdrop.id = "dt-dims-backdrop";
  backdrop.addEventListener("mousedown", () => hide());
  document.body.appendChild(backdrop);

  const root = document.createElement("div");
  root.id = "dt-dims";
  document.body.appendChild(root);

  root.appendChild(el("h3", "", "Canvas dimensions"));
  root.appendChild(
    el("div", "sub", "Real size of the object — the canvas matches its proportions."),
  );

  // Default unit (used for bare numbers; explicit suffixes override per field).
  // Two options → a segmented toggle, matching the sidebars' unit control.
  let unit: LengthUnit = "in";
  const unitToggle = createSegToggle(
    [
      { label: "ft / in", value: "in" },
      { label: "meters", value: "m" },
    ],
    unit,
    (v) => {
      const next = v as LengthUnit;
      // Convert each entered value from the old unit's reading into the new unit,
      // so the physical sizes stay put — only the displayed unit changes. The
      // W/D/H are lengths; the detail field is a density (re-derived through the
      // voxel size so per-inch ↔ per-cm preserves the real voxel size).
      for (const inp of [wIn, dIn, hIn]) {
        if (inp.value.trim() === "") continue;
        const inches = parseLength(inp.value, unit);
        if (inches !== null && inches > 0) inp.value = formatLengthIn(inches, next);
      }
      const density = Number(densIn.value);
      if (density > 0) {
        const voxelIn = densityToVoxelInches(density, unit);
        densIn.value = fmtDensity(voxelInchesToDensity(voxelIn, next));
      }
      unit = next;
      unitToggle.setActive(v);
      updatePlaceholders();
      recompute();
    },
  );
  unitToggle.el.style.flex = "1"; // fill the row beside the label
  root.appendChild(row("Units", unitToggle.el));

  const wIn = field("");
  const dIn = field("");
  const hIn = field("");
  root.appendChild(row("Depth", dIn));
  root.appendChild(row("Width", wIn));
  root.appendChild(row("Height", hIn));

  // Example placeholders track the selected units (incl. a sub-meter example).
  const EXAMPLES: Record<string, [string, string, string]> = {
    in: ['e.g. 48 3/4"', "e.g. 19.5", "e.g. 8'"],
    m: ["e.g. 1.2 m", "e.g. 0.75 m", "e.g. 2.4 m"],
  };
  function updatePlaceholders(): void {
    const [w, d, h] = EXAMPLES[unit] ?? EXAMPLES.in;
    wIn.placeholder = w;
    dIn.placeholder = d;
    hIn.placeholder = h;
    densLabel.textContent = unit === "m" ? "voxels per cm" : "voxels per inch";
  }

  // Voxel detail as a density (voxels per inch / per cm); the cell counts derive
  // from it. The unit toggle converts it to keep the physical voxel size put.
  const densIn = document.createElement("input");
  densIn.type = "number";
  densIn.min = "0.05";
  densIn.step = "0.5";
  densIn.value = "1";
  const detailRow = row("Detail", densIn);
  detailRow.classList.add("detail");
  const densLabel = el("span", "sub", "voxels per inch");
  detailRow.appendChild(densLabel);
  root.appendChild(detailRow);

  const preview = el("div", "preview", "Enter all three dimensions.");
  root.appendChild(preview);

  const apply = document.createElement("button") as HTMLButtonElement;
  apply.className = "primary";
  apply.textContent = "Apply";
  const cancel = document.createElement("button") as HTMLButtonElement;
  cancel.textContent = "Close";
  const btns = el("div", "btns");
  btns.append(cancel, apply);
  root.appendChild(btns);

  let pending: DimensionsResult | null = null;
  let onceApply: ((r: DimensionsResult) => void) | null = null;

  function recompute(): void {
    const parse = (inp: HTMLInputElement): number | null => {
      const v = inp.value.trim() === "" ? null : parseLength(inp.value, unit);
      inp.classList.toggle("bad", inp.value.trim() !== "" && (v === null || v <= 0));
      return v;
    };
    const w = parse(wIn);
    const d = parse(dIn);
    const h = parse(hIn);
    const density = Number(densIn.value); // voxels per inch / per cm
    densIn.classList.toggle("bad", densIn.value.trim() !== "" && !(density > 0));
    if (w === null || d === null || h === null || w <= 0 || d <= 0 || h <= 0) {
      preview.textContent = "Enter all three dimensions.";
      pending = null;
      apply.disabled = true;
      return;
    }
    if (!(density > 0)) {
      preview.textContent = "Enter a voxel density.";
      pending = null;
      apply.disabled = true;
      return;
    }
    const vox = densityToVoxelInches(density, unit); // requested voxel size (inches)
    // Don't let too fine a density blow past the cell cap (which would distort the
    // proportions as axes clamp unevenly): floor the voxel size so the longest
    // axis lands at MAX_CELLS, and flag when that kicks in.
    const longest = Math.max(w, d, h);
    const minVoxel = longest / MAX_CELLS;
    const voxel = Math.max(vox, minVoxel);
    const capped = voxel > vox * (1 + 1e-9);
    const clampCells = (real: number) =>
      Math.max(1, Math.min(MAX_CELLS, Math.round(real / voxel)));
    const cx = clampCells(w);
    const cy = clampCells(h);
    const cz = clampCells(d);
    const f = (inches: number) => formatLengthIn(inches, unit);
    preview.innerHTML =
      `<b>${cz} × ${cx} × ${cy}</b> voxels (D×W×H)<br>` +
      `1 voxel ≈ ${f(voxel)}${capped ? ` · capped at ${MAX_CELLS}/side` : ""}<br>` +
      `models ${f(cz * voxel)} × ${f(cx * voxel)} × ${f(cy * voxel)}`;
    pending = {
      cellsX: cx,
      cellsY: cy,
      cellsZ: cz,
      voxelInches: voxel,
      unit,
      realIn: { w, d, h }, // exact (inches) — parseLength returns canonical inches
    };
    apply.disabled = false;
  }

  for (const inp of [wIn, dIn, hIn, densIn]) inp.addEventListener("input", recompute);
  // (unit changes are handled by the toggle's onSelect above.)
  updatePlaceholders();
  apply.addEventListener("click", () => {
    if (pending) {
      (onceApply ?? opts.onApply)(pending);
      hide();
    }
  });
  cancel.addEventListener("click", hide);

  // Keyboard: while the box is open, Enter triggers the highlighted action
  // (Apply, a no-op when disabled by invalid input) and Escape closes it. On
  // document so it works whichever field has focus, and we stop the event so the
  // app's global (window) shortcuts don't also fire behind the box.
  document.addEventListener("keydown", (e) => {
    if (!root.classList.contains("open")) return;
    if (e.key === "Enter") {
      e.preventDefault();
      e.stopPropagation();
      apply.click(); // disabled → no-op
    } else if (e.key === "Escape") {
      e.preventDefault();
      e.stopPropagation();
      cancel.click();
    }
  });

  function hide(): void {
    root.classList.remove("open");
    backdrop.classList.remove("open");
    onceApply = null;
  }

  recompute();

  return {
    open(
      initial?: DimensionsInitial,
      onApply?: (r: DimensionsResult) => void,
    ): void {
      onceApply = onApply ?? null;
      // Prefill from the current canvas so the panel shows this model's own
      // values. Formatted with explicit units, so they round-trip through parse
      // regardless of the unit dropdown.
      if (initial) {
        unit = initial.unit;
        unitToggle.setActive(initial.unit);
        wIn.value = formatLengthIn(initial.widthIn, initial.unit);
        dIn.value = formatLengthIn(initial.depthIn, initial.unit);
        hIn.value = formatLengthIn(initial.heightIn, initial.unit);
        densIn.value = fmtDensity(voxelInchesToDensity(initial.voxelIn, initial.unit));
        updatePlaceholders();
      }
      backdrop.classList.add("open");
      root.classList.add("open");
      recompute();
      dIn.focus(); // first field (Depth) on open
    },
    close: hide,
    isOpen: () => root.classList.contains("open"),
  };
}

function el(tag: string, cls: string, text?: string): HTMLElement {
  const e = document.createElement(tag);
  if (cls) e.className = cls;
  if (text) e.textContent = text;
  return e;
}

function field(placeholder: string): HTMLInputElement {
  const i = document.createElement("input");
  i.type = "text";
  i.placeholder = placeholder;
  return i;
}

function row(label: string, control: HTMLElement): HTMLElement {
  const r = el("div", "row");
  r.appendChild(el("label", "", label));
  r.appendChild(control);
  return r;
}
